import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Loader2, Edit3, Trash2, Newspaper, BookOpen, ExternalLink, Linkedin } from 'lucide-react';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../context/AdminAuthContext';
import { cmsList, cmsDelete, linkedinStatus, linkedinOAuthStart } from '../lib/marketingApi';

const URLSEG_TO_TYPE = { blogs: 'blog', 'case-studies': 'case_study' };
const META = {
  blog: { title: 'Blogs', singular: 'Article', icon: Newspaper, publicBase: '/blog' },
  case_study: { title: 'Case Studies', singular: 'Case study', icon: BookOpen, publicBase: '/case-studies' },
};
const FILTERS = [{ k: '', label: 'All' }, { k: 'published', label: 'Published' }, { k: 'draft', label: 'Drafts' }];

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); } catch (e) { return iso; }
}

function LinkedInControl() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { linkedinStatus().then(setS).catch(() => setS(null)); }, []);
  const connect = async () => {
    setBusy(true);
    try { const d = await linkedinOAuthStart(); window.location.href = d.url; }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not start LinkedIn connect'); setBusy(false); }
  };
  if (!s) return null;
  if (s.live) {
    return (
      <span data-testid="li-header-connected" className="inline-flex h-9 items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 text-xs font-semibold text-emerald-700">
        <Linkedin className="h-4 w-4" /> {s.org_name || 'Connected'}
      </span>
    );
  }
  if (!s.app_configured) return null;
  return (
    <button onClick={connect} disabled={busy} data-testid="li-header-connect-btn"
      className="inline-flex h-9 items-center gap-1.5 rounded-md border border-[#0A66C2] px-3 text-xs font-semibold text-[#0A66C2] hover:bg-[#0A66C2]/5 disabled:opacity-60">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Linkedin className="h-4 w-4" />} Connect LinkedIn
    </button>
  );
}

export default function ContentList() {
  const { type: urlType } = useParams();
  const type = URLSEG_TO_TYPE[urlType] || 'blog';
  const meta = META[type];
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'editor';
  const [sp, setSp] = useSearchParams();

  useEffect(() => {
    const li = sp.get('linkedin');
    if (!li) return;
    if (li === 'connected') toast.success('LinkedIn connected — posts will now go live to your page 🎉');
    else if (li === 'error') toast.error(`LinkedIn connection failed: ${sp.get('msg') || 'unknown error'}`);
    sp.delete('linkedin'); sp.delete('msg'); setSp(sp, { replace: true });
  }, []); // eslint-disable-line

  const [items, setItems] = useState(null);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const d = await cmsList(type, status || undefined);
      setItems(d.items || []);
    } catch (e) { toast.error('Failed to load content'); setItems([]); }
  }, [type, status]);

  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    const item = confirmDelete;
    if (!item) return;
    setBusy(item.id);
    try { await cmsDelete(type, item.id); toast.success('Deleted'); setConfirmDelete(null); load(); }
    catch (e) { toast.error('Delete failed'); } finally { setBusy(null); }
  };

  const Icon = meta.icon;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="content-list">
      <AdminHeader
        active="content"
        actions={(
          <div className="flex items-center gap-2">
            <LinkedInControl />
            {canEdit && (
              <button onClick={() => navigate(`/admin/content/${urlType}/new`)} data-testid="content-new-btn"
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#002B87]">
                <Plus className="h-4 w-4" /> New {meta.singular.toLowerCase()}
              </button>
            )}
          </div>
        )}
      />
      <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
        <div className="mb-4 flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0035A8]/10 text-[#0035A8]"><Icon className="h-4.5 w-4.5" /></span>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">{meta.title}</h1>
            <p className="text-sm text-slate-500">Create, edit and publish {meta.title.toLowerCase()} for the marketing site</p>
          </div>
        </div>

        <div className="mb-4 inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1" data-testid="content-filters">
          {FILTERS.map((f) => (
            <button key={f.k} onClick={() => setStatus(f.k)} data-testid={`content-filter-${f.label.toLowerCase()}`}
              className={`rounded-md px-3.5 py-1.5 text-sm font-semibold transition-all ${status === f.k ? 'bg-[#0035A8] text-white' : 'text-slate-500 hover:text-slate-800'}`}>
              {f.label}
            </button>
          ))}
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="hidden grid-cols-12 gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid">
            <div className="col-span-4">Title</div>
            <div className="col-span-2">{type === 'blog' ? 'Category' : 'Segment'}</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1 text-right">Views</div>
            <div className="col-span-1 text-right">Engmt</div>
            <div className="col-span-2">Updated</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>

          {items === null ? (
            <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-slate-400">
              <Icon className="h-8 w-8" />
              <p className="text-sm">No {meta.title.toLowerCase()} yet.</p>
            </div>
          ) : items.map((it) => (
            <div key={it.id} data-testid={`content-row-${it.id}`} className="grid grid-cols-1 gap-2 border-b border-slate-100 px-5 py-3.5 md:grid-cols-12 md:items-center md:gap-3">
              <button onClick={() => navigate(`/admin/content/${urlType}/${it.id}`)} className="col-span-4 min-w-0 text-left">
                <div className="truncate text-sm font-semibold text-slate-900 hover:text-[#0035A8]">{it.title}</div>
                <div className="truncate text-xs text-slate-400">/{it.slug}</div>
              </button>
              <div className="col-span-2 text-sm text-slate-600">{it.category || it.segment || '—'}</div>
              <div className="col-span-1">
                <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${it.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {it.status === 'published' ? 'Live' : 'Draft'}
                </span>
              </div>
              <div className="col-span-1 text-right text-sm font-semibold text-slate-700" data-testid={`content-views-${it.id}`}>{(it.stats?.views ?? it.views ?? 0).toLocaleString()}</div>
              <div className="col-span-1 text-right text-sm font-semibold text-[#0035A8]" title="Clicks on CTA + related + share" data-testid={`content-engagement-${it.id}`}>{((it.stats?.cta_clicks || 0) + (it.stats?.related_clicks || 0) + (it.stats?.share_clicks || 0)).toLocaleString()}</div>
              <div className="col-span-2 text-xs text-slate-500">{fmt(it.updated_at)}</div>
              <div className="col-span-1 flex items-center justify-end gap-1">
                {it.status === 'published' && (
                  <a href={`${meta.publicBase}/${it.slug}`} target="_blank" rel="noreferrer" title="View live" className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-[#0035A8]"><ExternalLink className="h-4 w-4" /></a>
                )}
                <button onClick={() => navigate(`/admin/content/${urlType}/${it.id}`)} title="Edit" data-testid={`content-edit-${it.id}`} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-[#0035A8]"><Edit3 className="h-4 w-4" /></button>
                {canEdit && (
                  <button onClick={() => setConfirmDelete(it)} disabled={busy === it.id} title="Delete" data-testid={`content-delete-${it.id}`} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="delete-confirm-modal" onClick={() => setConfirmDelete(null)}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600"><Trash2 className="h-5 w-5" /></span>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Delete “{confirmDelete.title}”?</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                  This permanently deletes the {meta.singular.toLowerCase()}{confirmDelete.status === 'published' ? ' and removes it from the website' : ''}. This cannot be undone. Are you sure you want to go ahead?
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} data-testid="delete-confirm-cancel" className="inline-flex h-10 items-center rounded-md border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={remove} disabled={busy === confirmDelete.id} data-testid="delete-confirm-yes" className="inline-flex h-10 items-center gap-1.5 rounded-md bg-rose-600 px-4 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60">{busy === confirmDelete.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Yes, delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
